import { useTranslation } from 'react-i18next';
import { ReactComponent as TrashSvg } from 'assets/svg/trash.svg';
import { ReactComponent as EyeSvg } from 'assets/svg/eye.svg';
import { useState } from 'react';
import customTwMerge from 'utils/twMerge';
import { CCheckboxWithLabel, PresetCard } from 'components/molecules';
import {
  useDeleteNotificationsMutation,
  useNotificationsQuery,
  useReadNotificationsMutation,
} from 'services/notification';
import Notification from './notification';
import { enqueueSnackbar } from 'notistack';
import { CLottie } from 'components/atoms';
import { useAppSelector } from 'hooks/redux';

type Props = {
  closeDrawer: () => void;
  openUpdateCardDrawer: (membership: {
    patientId?: number;
    subscriptionGroupId?: number;
    subscriptionId?: number;
    patientSubscriptionId?: number;
    cancellationId?: number;
  }) => void;
};

export default function Notifications({ closeDrawer, openUpdateCardDrawer }: Props) {
  const { t } = useTranslation(['translation', 'common']);
  const user = useAppSelector((state) => state.auth.user);
  const [selectedNotificationIds, setSelectedNotificationIds] = useState<number[]>([]);
  const initialCheckedState = {
    isSelectAll: false,
    isAnyNotification: false,
    isAnyUnreadNotification: false,
  };
  const [checked, setChecked] = useState(initialCheckedState);
  const { data, isLoading, isFetching, isSuccess } = useNotificationsQuery(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    { profile: user! },
    { refetchOnMountOrArgChange: true }
  );
  const [readNotification, { isLoading: isLoadingReadNotification }] = useReadNotificationsMutation();
  const [deleteNotification, { isLoading: isLoadingDeleteNotification }] =
    useDeleteNotificationsMutation();

  const handleOpenUpdateCardDrawer = (membership: {
    patientId?: number;
    subscriptionGroupId?: number;
    subscriptionId?: number;
    patientSubscriptionId?: number;
    cancellationId?: number;
  }) => {
    closeDrawer(); // firstly close drawers
    openUpdateCardDrawer(membership); // and then open update card drawer
  };

  const handleSelectAll = (notifications: NotificationResp[]) => {
    setChecked(({ isSelectAll }) => {
      isSelectAll
        ? setSelectedNotificationIds([])
        : setSelectedNotificationIds(notifications.map((notification) => notification.id));

      return {
        isSelectAll: !isSelectAll,
        isAnyNotification: !isSelectAll,
        isAnyUnreadNotification: !isSelectAll,
      };
    });
  };

  const handleClickNotification = (
    notifications: NotificationResp[],
    notification: NotificationResp
  ) => {
    setSelectedNotificationIds((prev) => {
      let lengthOfNewSelectedNotifications = 0;
      const newSelectedNotificationIds = [];

      prev.includes(notification.id)
        ? (lengthOfNewSelectedNotifications = newSelectedNotificationIds.push(
            ...prev.filter((id) => id !== notification.id)
          ))
        : (lengthOfNewSelectedNotifications = newSelectedNotificationIds.push(
            ...prev,
            notification.id
          ));

      setChecked({
        isSelectAll: lengthOfNewSelectedNotifications === notifications.length,
        isAnyNotification: lengthOfNewSelectedNotifications > 0,
        isAnyUnreadNotification: newSelectedNotificationIds.some((selectedId) =>
          notifications.find(
            (notification) => notification.id === selectedId && notification.status === 1
          )
        ),
      });

      return newSelectedNotificationIds;
    });
  };

  const handleReadNotification = async () => {
    const { isSuccess, exception } = await readNotification({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      profile: user!,
      data: {
        idKeys: selectedNotificationIds,
      },
    }).unwrap();

    if (isSuccess) {
      setSelectedNotificationIds([]);
      setChecked(initialCheckedState);
    } else {
      enqueueSnackbar(exception.message || t('error.unexpected'), { variant: 'error' });
    }
  };

  const handleDeleteNotification = async () => {
    const { isSuccess, exception } = await deleteNotification({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      profile: user!,
      data: {
        idKeys: selectedNotificationIds,
      },
    }).unwrap();

    if (isSuccess) {
      setSelectedNotificationIds([]);
      setChecked(initialCheckedState);
    } else {
      enqueueSnackbar(exception.message || t('error.unexpected'), { variant: 'error' });
    }
  };

  const handleSingleReadNotification = async (id: number) => {
    try {
      await readNotification({
        profile: user!,
        data: {
          idKeys: [id],
        },
      }).unwrap();
    } catch (error) {
      enqueueSnackbar(error?.message || t('error.unexpected'), { variant: 'error' });
    }
  };

  if (isLoading) return <CLottie isFullPage />;

  if (isSuccess) {
    const notifications = data.data;

    if (notifications.length === 0) return <PresetCard type="notification" containerClassName="mt-5" />;

    return (
      <div className="relative flex h-full flex-col">
        <div className="sticky inset-x-0 top-0 z-10 px-5 py-4">
          <div className="flex w-full justify-between px-4">
            <div
              className={customTwMerge(
                'flex',
                checked.isSelectAll ? 'text-secondary-800' : 'text-neutral-400',
                isFetching || isLoadingDeleteNotification || isLoadingReadNotification
                  ? 'pointer-events-none text-neutral-200'
                  : ''
              )}
            >
              <CCheckboxWithLabel
                label={t('common:select-all')}
                iconType="rounded"
                iconClassName="w-4 h-4"
                checkboxClassName="text-inherit"
                labelClassName={customTwMerge(
                  'text-body16',
                  isFetching || isLoadingDeleteNotification || isLoadingReadNotification
                    ? 'text-neutral-200'
                    : 'text-neutral-400'
                )}
                checked={checked.isSelectAll}
                onChange={() => handleSelectAll(notifications)}
              />
            </div>

            <div className="flex gap-5 text-label16">
              <button
                className={customTwMerge(
                  'flex items-center justify-center gap-1',
                  checked.isAnyNotification
                    ? 'hover-transition text-secondary-800'
                    : 'text-neutral-200',
                  isLoadingDeleteNotification || isLoadingReadNotification
                    ? 'pointer-events-none text-neutral-200'
                    : ''
                )}
                onClick={handleDeleteNotification}
                disabled={!checked.isAnyNotification}
              >
                <TrashSvg className="h-4 w-4" />
                <span>{t('common:delete')}</span>
              </button>

              <button
                className={customTwMerge(
                  'flex items-center justify-center gap-1',
                  checked.isAnyUnreadNotification
                    ? 'hover-transition text-secondary-800'
                    : 'text-neutral-200',
                  isLoadingDeleteNotification || isLoadingReadNotification
                    ? 'pointer-events-none text-neutral-200'
                    : ''
                )}
                onClick={handleReadNotification}
                disabled={!checked.isAnyUnreadNotification}
              >
                <EyeSvg className="h-4 w-4" />
                <span>{t('common:read')}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto">
          {(isFetching || isLoadingReadNotification || isLoadingDeleteNotification) && (
            <CLottie
              isFullPage
              containerStyle="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] z-10"
            />
          )}

          <div
            className={`flex flex-col gap-2 px-5 pb-4 pt-2 ${
              isFetching || isLoadingReadNotification || isLoadingDeleteNotification ? 'opacity-30' : ''
            }`}
          >
            {notifications.map((notification) => (
              <Notification
                key={notification.id}
                isChecked={selectedNotificationIds.includes(notification.id)}
                onClick={() => handleClickNotification(notifications, notification)}
                notification={notification}
                closeDrawer={closeDrawer}
                onMarkAsRead={handleSingleReadNotification}
                openUpdateCardDrawer={handleOpenUpdateCardDrawer}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return null;
}

